import {
  Accordion,
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Info, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../../providers/AuthProvider';

function capitalizeFirst(str?: string) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
}

const CATEGORIES = [
  {
    title: 'Informazioni Personali',
    fields: [
      { key: 'fiscal_code', label: 'Codice Fiscale' },
      { key: 'order_of_psychologists', label: 'Ordine degli psicologi' },
      { key: 'order_number', label: 'Numero di appartenenza' },
    ],
  },
  {
    title: 'Crediti ECM',
    fields: [
      { key: 'role', label: 'Ruolo' },
      { key: 'contract', label: 'Tipo contratto' },
      { key: 'title', label: 'Titolo' },
      { key: 'profession', label: 'Professione' },
      { key: 'disciple', label: 'Disciplina' },
    ],
  },
  {
    title: 'Pagina Professionisti',
    fields: [
      { key: 'website', label: 'Sito Web' },
      { key: 'region', label: 'Regione' },
      { key: 'profile_photo_path', label: 'Url foto profilo' },
    ],
  },
  {
    title: 'Dati di Fatturazione',
    fields: [
      { key: 'address', label: 'Indirizzo di fatturazione' },
      { key: 'vat_number', label: 'P.IVA' },
      { key: 'sdi_number', label: 'SDI' },
    ],
  },
];

export function ProfileSection() {
  const user = useAuthUser();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 1408px)');
  const navigate = useNavigate();

  const hasMissing = CATEGORIES.some(({ fields }) =>
    fields.some(({ key }) => {
      const val = user?.[key as keyof typeof user];
      return val === undefined || val === null || val === '';
    })
  );

  const fullName =
    user?.full_name ||
    `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  return (
    <>
      <Card shadow='sm' withBorder mb='md'>
        <Group>
          <Box px={20}>
            <User size={32} color='#ccc' />
          </Box>
          <Stack gap={4}>
            <Title order={3}>{fullName || 'Profilo'}</Title>
            {user?.type && (
              <Text size='sm' c={theme.primaryColor}>
                {capitalizeFirst(user.type)}
              </Text>
            )}
            <Text size='sm' c='dimmed'>
              {user?.email}
            </Text>
          </Stack>
        </Group>
      </Card>

      {isMobile ? (
        <Accordion variant='contained' defaultValue={[]} multiple>
          <Accordion.Item value='i-tuoi-dati'>
            <Accordion.Control>
              <Group justify='space-between'>
                <Flex align='center' justify='space-between' w='100%'>
                  <Group gap='xs'>
                    <Title order={4}>I tuoi dati</Title>
                    {hasMissing && <Info size={16} color='red' />}
                  </Group>
                  <Button
                    mr='sm'
                    onClick={() => navigate('/profile')}
                    variant='outline'
                    size='xs'
                  >
                    Modifica
                  </Button>
                </Flex>
              </Group>
            </Accordion.Control>

            <Accordion.Panel>
              <Card shadow='sm' withBorder mt='xs'>
                {hasMissing && (
                  <Alert
                    title='Attenzione: alcuni dati sono incompleti'
                    color='red'
                    mb='xs'
                  />
                )}

                <Stack gap='sm'>
                  {CATEGORIES.map(({ title, fields }) => (
                    <div key={title}>
                      <Title order={5}>{title}</Title>
                      <Stack gap={2} mt={4}>
                        {fields.map(({ key, label }) => {
                          const value = user?.[key as keyof typeof user];
                          const missing =
                            value === undefined ||
                            value === null ||
                            value === '';

                          return (
                            <Group key={key} gap='xs'>
                              <Text fw={500} size='sm'>
                                {label}:
                              </Text>
                              <Text size='sm' c={missing ? 'red' : undefined}>
                                {missing ? 'Non compilato' : String(value)}
                              </Text>
                            </Group>
                          );
                        })}
                      </Stack>
                      <Divider mt='sm' />
                    </div>
                  ))}
                </Stack>
              </Card>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : (
        <Card shadow='sm' withBorder>
          <Group justify='space-between' mb='xs'>
            <Flex gap='xs' align='center' justify='space-between' w='100%'>
              <Group gap='xs'>
                <Title order={4}>I tuoi dati</Title>
                {hasMissing && <Info size={16} color='red' />}
              </Group>
              <Button
                onClick={() => navigate('/profile')}
                variant='outline'
                size='xs'
              >
                Modifica
              </Button>
            </Flex>
          </Group>

          {hasMissing && (
            <Alert
              title='Attenzione: alcuni dati sono incompleti'
              color='red'
              mb='xs'
            />
          )}

          <Stack gap='sm' mt='sm'>
            {CATEGORIES.map(({ title, fields }) => (
              <div key={title}>
                <Title order={5}>{title}</Title>
                <Stack gap={2} mt={4}>
                  {fields.map(({ key, label }) => {
                    const value = user?.[key as keyof typeof user];
                    const missing =
                      value === undefined || value === null || value === '';

                    return (
                      <Group key={key} gap='xs'>
                        <Text fw={500} size='sm'>
                          {label}:
                        </Text>
                        <Text size='sm' c={missing ? 'red' : undefined}>
                          {missing ? 'Non compilato' : String(value)}
                        </Text>
                      </Group>
                    );
                  })}
                </Stack>
                <Divider mt='sm' />
              </div>
            ))}
          </Stack>
        </Card>
      )}
    </>
  );
}
